import { GetNextTicketNumberAPI } from "api";
import { useFetchClinics } from "hooks/useFetchClinics";
import { useFetchPatients } from "hooks/useFetchPatients";
import { useFetchPhysicians } from "hooks/useFetchPhysicians";
import PatientAddModal from "modals/PatientAddModal";
import TicketPrint from "modules/reporting/TicketPrint/TicketPrint";
import {
  closestCorners,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  FloatingLabel,
  Form,
  InputGroup,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import { useFieldArray, useFormContext } from "react-hook-form";
import { BsPersonAdd } from "react-icons/bs";
import { MdOutlineLibraryAddCheck } from "react-icons/md";
import { AiOutlineDelete } from "react-icons/ai";
import { useLocation, useNavigate } from "react-router-dom";
import ActionButton from "components/common/ActionButton";
import { useFetchServices } from "hooks/useFetchServices";

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td
        {...listeners}
        {...attributes}
        style={{
          cursor: "grab",
          textAlign: "center",
          width: "10px",
          // paddingTop: "15px",
        }}
      >
        ☰
      </td>
      {children}
    </tr>
  );
};

const BasicInformation = () => {
  const {
    register,
    setValue,
    control,
    watch,
    formState: { errors },
    clearErrors,
  } = useFormContext();
  const { state } = useLocation();
  const navigate = useNavigate();
  const ticketDate = watch("ticketDate");
  const [ticketNumberLoading, setTicketNumberLoading] = useState(false);
  const [isOpenPatientModal, setIsOpenPatientModal] = useState(false);
  const [modalPatientContent, setModalPatientContent] = useState({});

  const {
    fields: services,
    append: appendServices,
    remove: removeServices,
    move,
  } = useFieldArray({
    control,
    name: "services",
  });

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  // useNavigationPrompt(isDirty);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = services.findIndex((item) => item.id === active.id);
      const newIndex = services.findIndex((item) => item.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  const printRef = useRef();
  const [ticketData, setTicketData] = useState(state || null);

  const handlePrint = () => {
    printRef.current.handlePrint();
  };

  const handleOpenPatientModal = () => {
    setModalPatientContent({});
    setIsOpenPatientModal(true);
  };

  const {
    data: patientsData,
    isLoading: patientsLoading,
    isFetching: patientsFetching,
  } = useFetchPatients();

  const { data: clinicsData } = useFetchClinics();
  const { data: servicesData, isLoading: servicesLoading } = useFetchServices();

  const {
    data: physiciansData,
    isLoading: physiciansLoading,
    isFetching: physiciansFetching,
  } = useFetchPhysicians(clinicsData?.data?.data[0]?.id);

  let patientsList = patientsData?.data?.data;
  let physiciansList = physiciansData?.data?.data;
  let servicesList = servicesData?.data?.data;

  useEffect(() => {
    if (!state) {
      const today = new Date().toISOString().split("T")[0];
      setValue("ticketDate", today);
    }
  }, [setValue, state]);

  useEffect(() => {
    const loadTicketNumber = async () => {
      if (!ticketDate) return;

      try {
        setTicketNumberLoading(true);

        const res = await GetNextTicketNumberAPI.get({
          params: { date: ticketDate },
        });

        const ticketNo = res?.data?.data;

        if (ticketNo) {
          setValue("ticketNumber", ticketNo);
        }
      } catch (error) {
        console.error("Failed to fetch ticket number", error);
      } finally {
        setTicketNumberLoading(false);
      }
    };

    if (!state) {
      loadTicketNumber();
    }
  }, [ticketDate, setValue, state]);

  useEffect(() => {
    if (physiciansList?.length && !watch("physicianId")) {
      setValue("physicianId", physiciansList[0].id);
    }

    if (!watch("service")) {
      setValue("service", "Medical Consultancy");
    }

    if (!watch("status")) {
      setValue("status", "Issued");
    }
  }, [physiciansList, setValue]);

  if (
    patientsLoading ||
    physiciansLoading ||
    ticketNumberLoading ||
    servicesLoading
  ) {
    return <Spinner variant="primary" animation="border" />;
  }

  return (
    <>
      <Card className="mb-3">
        <Card.Header as="h6" className="bg-body-tertiary">
          Basic information
        </Card.Header>
        <Card.Body>
          <Row className="gx-2 gy-3">
            <Col md="6">
              <Form.Group>
                <FloatingLabel
                  controlId="floatingInput"
                  label={
                    <span>
                      Ticket Number <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  className=""
                >
                  <Form.Control
                    type="text"
                    placeholder="name@example.com"
                    autoComplete="off"
                    readOnly
                    isInvalid={!!errors.ticketNumber}
                    {...register("ticketNumber")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.ticketNumber?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <FloatingLabel
                  controlId="floatingInput"
                  label={
                    <span>
                      Date <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  className=""
                >
                  <Form.Control
                    type="date"
                    placeholder="name@example.com"
                    autoComplete="off"
                    isInvalid={!!errors.ticketDate}
                    {...register("ticketDate")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.ticketDate?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <FloatingLabel controlId="floatingSelectGrid" label="Physician">
                  <Form.Select
                    aria-label="physicianId"
                    isInvalid={!!errors.physicianId}
                    {...register("physicianId")}
                  >
                    <option value="">Select</option>
                    {physiciansList?.map((pt) => (
                      <option value={pt.id} key={pt.id}>
                        {pt.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.physicianId?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>

            <Col md="6">
              <InputGroup>
                <FloatingLabel controlId="floatingSelectGrid" label="Patient">
                  <Form.Select
                    aria-label="patientId"
                    isInvalid={!!errors.patientId}
                    {...register("patientId")}
                  >
                    <option value="">Select</option>
                    {patientsList?.map((pt) => (
                      <option value={pt.id} key={pt.id}>
                        {pt.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.patientId?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <OverlayTrigger
                  overlay={
                    <Tooltip
                      style={{ position: "fixed" }}
                      id="overlay-trigger-example"
                    >
                      Add Patient
                    </Tooltip>
                  }
                >
                  <Button
                    onClick={handleOpenPatientModal}
                    variant="primary"
                    style={{ maxHeight: "58px", width: "54px" }}
                  >
                    <BsPersonAdd size={22} />
                  </Button>
                </OverlayTrigger>
              </InputGroup>
            </Col>

            {/* <Col md="6">
              <Form.Group>
                <FloatingLabel controlId="floatingSelectGrid" label="Service">
                  <Form.Select
                    aria-label="service"
                    isInvalid={!!errors.service}
                    {...register("service")}
                  >
                    <option value="">Select</option>
                    <option value="Medical Consultancy">
                      Medical Consultancy
                    </option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.service?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <FloatingLabel controlId="floatingSelectGrid" label="Status">
                  <Form.Select
                    aria-label="status"
                    isInvalid={!!errors.status}
                    {...register("status")}
                  >
                    <option value="">Select</option>
                    {["Issued", "Processing", "Completed", "No Show"].map(
                      (opt) => (
                        <option value={opt} key={opt}>
                          {opt}
                        </option>
                      ),
                    )}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.status?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>

            <Col md="6">
              <Form.Group>
                <FloatingLabel
                  controlId="floatingInput"
                  label={
                    <span>
                      Amount <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  className=""
                >
                  <Form.Control
                    type="number"
                    placeholder="name@example.com"
                    autoComplete="off"
                    min="0"
                    isInvalid={!!errors.amount}
                    {...register("amount", { valueAsNumber: true })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.amount?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col> */}
            <Col md="6"></Col>
            <Col md="6">
              <Form.Group>
                <FloatingLabel
                  controlId="floatingInput"
                  label={
                    <span>
                      Paid Amount
                      {/* <span style={{ color: "red" }}>*</span> */}
                    </span>
                  }
                  className=""
                >
                  <Form.Control
                    type="number"
                    // min="0"
                    placeholder="name@example.com"
                    autoComplete="off"
                    isInvalid={!!errors.amountPaid}
                    {...register("amountPaid")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.amountPaid?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>

            {/* <Col md="12">
            <Form.Check
              type="switch"
              id="defaultSwitch"
              label="Active"
              defaultChecked={!state}
              isInvalid={!!errors.active}
              {...register("active")}
            />
          </Col> */}
          </Row>

          <Row className="mt-2">
            <Col md="12">
              <h6>Services</h6>
            </Col>
          </Row>

          <Row className="gx-2 gy-3">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={services.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <Table responsive>
                  <thead className="bg-200">
                    <tr>
                      {/* Drag */}
                      <th style={{ width: "40px" }}></th>

                      {/* Order */}
                      <th
                        className="text-center fs-10"
                        style={{ width: "70px" }}
                      >
                        Order
                      </th>

                      {/* Service */}
                      <th className="fs-10">Title</th>

                      {/* Status */}
                      {/* <th className="fs-10" style={{ width: "180px" }}>
                        Status
                      </th> */}

                      {/* Amount */}
                      <th className="fs-10" style={{ width: "150px" }}>
                        Amount
                      </th>

                      {/* Add */}
                      <th
                        scope="col"
                        style={{ padding: "9px", width: "10px" }}
                        className=" text-end"
                      >
                        <ActionButton
                          icon="plus"
                          variant="success"
                          title="Add"
                          className="px-1 py-0"
                          onClick={() =>
                            appendServices({
                              serviceName: "",
                              serviceFee: "",
                            })
                          }
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {services.map((item, index) => (
                      <SortableItem key={item.id} id={item.id}>
                        {/* Order */}
                        <td className="text-center align-middle">
                          {index + 1}
                        </td>

                        {/* Service */}
                        <td style={{ padding: "5px" }}>
                          <Form.Group>
                            {/* <Form.Select
                              isInvalid={
                                !!errors.services?.[index]?.serviceName
                              }
                              {...register(`services.${index}.serviceName`)}
                            >
                              <option value="">Select</option>
                              {servicesList?.map((ser, index) => (
                                <option value={ser?.name} key={index}>
                                  {ser?.name}
                                </option>
                              ))}
                            </Form.Select> */}

                            <Form.Select
                              isInvalid={
                                !!errors.services?.[index]?.serviceName
                              }
                              {...register(`services.${index}.serviceName`)}
                              onChange={(e) => {
                                const selectedService = servicesList?.find(
                                  (ser) => ser.name === e.target.value,
                                );

                                // set selected service name
                                setValue(
                                  `services.${index}.serviceName`,
                                  e.target.value,
                                );

                                // auto set fee
                                setValue(
                                  `services.${index}.serviceFee`,
                                  selectedService?.level || "",
                                );
                              }}
                            >
                              <option value="">Select</option>

                              {servicesList?.map((ser, idx) => (
                                <option value={ser?.name} key={idx}>
                                  {ser?.name}
                                </option>
                              ))}
                            </Form.Select>

                            <Form.Control.Feedback type="invalid">
                              {errors.services?.[index]?.serviceName?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </td>

                        {/* Status */}
                        {/* <td style={{ padding: "5px" }}>
                          <Form.Group>
                            <Form.Select
                              isInvalid={!!errors.services?.[index]?.status}
                              {...register(`services.${index}.status`)}
                            >
                              <option value="Issued">Issued</option>

                              <option value="Processing">Processing</option>

                              <option value="Completed">Completed</option>

                              <option value="No Show">No Show</option>
                            </Form.Select>
                          </Form.Group>
                        </td> */}

                        {/* Amount */}
                        <td style={{ padding: "5px" }}>
                          <Form.Group>
                            <Form.Control
                              type="number"
                              min="0"
                              placeholder="Amount"
                              isInvalid={!!errors.services?.[index]?.serviceFee}
                              {...register(`services.${index}.serviceFee`, {
                                required: "Amount is required",
                                valueAsNumber: true,
                              })}
                            />

                            <Form.Control.Feedback type="invalid">
                              {errors.services?.[index]?.serviceFee?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </td>

                        {/* Delete */}

                        <td style={{ padding: "5px" }} className="text-center">
                          <ActionButton
                            icon="trash-alt"
                            title="Delete"
                            variant="danger"
                            className="px-1 py-0"
                            onClick={() => removeServices(index)}
                          />
                        </td>
                      </SortableItem>
                    ))}
                  </tbody>
                </Table>
              </SortableContext>
            </DndContext>
          </Row>

          <Row className="gx-2 gy-3 my-1">
            <Col className="d-flex justify-content-end">
              {/* <Button
                variant="primary"
                type="button"
                className="px-2 me-3"
                onClick={handlePrint}
              >
                🖨 Preview
              </Button> */}

              <Button
                variant="link"
                className="text-secondary fw-medium p-0 me-3"
                type="button"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Ok & Preview
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <PatientAddModal
        isOpenPatientModal={isOpenPatientModal}
        setIsOpenPatientModal={setIsOpenPatientModal}
        modalPatientContent={modalPatientContent}
      />

      <div style={{ display: "none" }}>
        <TicketPrint ref={printRef} ticket={ticketData} />
      </div>
    </>
  );
};

export default BasicInformation;
